import _extends from '@babel/runtime/helpers/extends';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import { autoUpdate, computePosition, flip, shift, limitShift, offset, arrow } from '@floating-ui/dom';
import classnames from 'classnames';
import React__default, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import useActiveElement from '../hooks/useActiveElement.js';
import { newLocation } from '../utils/FloatUILocation.js';
import FocusTrap from '../utils/FocusTrap.js';
import alertUserOfDeprecatedProps from '../utils/deprecation.js';
import Overlay from './Overlay.js';

var _excluded = ["className", "content", "children", "data-testid", "isVisible", "location", "type", "size", "onRequestClose", "onRequestOpen"];
var Popover = function Popover(_ref) {
  var className = _ref.className,
    content = _ref.content,
    children = _ref.children,
    dataTestId = _ref['data-testid'],
    isVisible = _ref.isVisible,
    _ref$location = _ref.location,
    location = _ref$location === void 0 ? 'none' : _ref$location,
    _ref$type = _ref.type,
    type = _ref$type === void 0 ? 'hoverAndClick' : _ref$type,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'normal' : _ref$size,
    onRequestClose = _ref.onRequestClose,
    onRequestOpen = _ref.onRequestOpen,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  alertUserOfDeprecatedProps('Popover', otherProps);
  if (otherProps.width) {
    console.error("Hey Canvas User! The width property is no longer supported in the Popover component. Try size=\"dense, normal, or expanded\" instead.");
  }
  if (otherProps.openOn) {
    console.error("Hey Canvas User! The openOn property is no longer supported in the Popover component. Try type=\"hoverandClick, clickOnly, or hoverOnly\" instead.");
  }
  var _useState = useState(isVisible),
    _useState2 = _slicedToArray(_useState, 2),
    isPopoverOpen = _useState2[0],
    setIsPopoverOpen = _useState2[1];
  var _useState3 = useState(type),
    _useState4 = _slicedToArray(_useState3, 2),
    clickedType = _useState4[0],
    setClickedType = _useState4[1];
  var arrowRef = useRef(null);
  var popperRef = useRef(null);
  var popoverContentRef = useRef(null);
  var cleanupFunction = useRef(null);
  var _useState5 = useState(location),
    _useState6 = _slicedToArray(_useState5, 2),
    popperLocation = _useState6[0],
    setPopperLocation = _useState6[1];
  var isClickOnly = type === 'clickOnly';

  // allow isVisible to be dynamically updated
  useEffect(function () {
    setIsPopoverOpen(isVisible);
  }, [isVisible]);
  useEffect(function () {
    if (isPopoverOpen && location !== 'none') {
      var _popoverContentRef$cu;
      cleanupFunction.current && cleanupFunction.current();
      popoverContentRef === null || popoverContentRef === void 0 ? void 0 : (_popoverContentRef$cu = popoverContentRef.current) === null || _popoverContentRef$cu === void 0 ? void 0 : _popoverContentRef$cu.setAttribute('data-show', '');
      if (location !== 'none') {
        var arrowEl = arrowRef === null || arrowRef === void 0 ? void 0 : arrowRef.current;
        var arrowLen = arrowEl === null || arrowEl === void 0 ? void 0 : arrowEl.offsetWidth;

        // Get half the arrow box's hypotenuse length
        var floatingOffset = Math.sqrt(2 * Math.pow(arrowLen, 2)) / 2;
        cleanupFunction.current = autoUpdate(popperRef.current, popoverContentRef.current, function () {
          popperRef.current && popoverContentRef.current && computePosition(popperRef.current, popoverContentRef.current, {
            middleware: [flip(), shift({
              limiter: limitShift()
            }), offset(floatingOffset), arrow({
              element: arrowEl
            })],
            placement: newLocation(location)
          }).then(function (_ref2) {
            var x = _ref2.x,
              y = _ref2.y,
              middlewareData = _ref2.middlewareData,
              placement = _ref2.placement;
            if (popoverContentRef !== null && popoverContentRef !== void 0 && popoverContentRef.current) {
              Object.assign(popoverContentRef.current.style, {
                left: "".concat(x - 10, "px"),
                top: "".concat(y - 10, "px")
              });
            }
            if (location !== 'none') {
              setPopperLocation(placement);
            }
            var side = placement.split('-')[0];
            var staticSide = {
              bottom: 'top',
              left: 'right',
              right: 'left',
              top: 'bottom'
            }[side];
            if (middlewareData.arrow) {
              var _middlewareData$arrow = middlewareData.arrow,
                _x = _middlewareData$arrow.x,
                _y = _middlewareData$arrow.y;
              //Format to default empty in the case of top-left, top-right, bottom-left, and bottom-right
              //this way the .scss folder can override the values otherwise these middleware values get
              //applied last
              var paddingOffset = newLocation(location).includes('start') || newLocation(location).includes('end');
              Object.assign(arrowEl.style, _defineProperty({
                bottom: '',
                left: paddingOffset ? '' : "".concat(_x, "px"),
                right: '',
                top: paddingOffset ? '' : "".concat(_y, "px")
              }, staticSide, "".concat(-arrowLen / 2, "px")));
            }
          });
        });
      }
    } else {
      var _popoverContentRef$cu2;
      popoverContentRef === null || popoverContentRef === void 0 ? void 0 : (_popoverContentRef$cu2 = popoverContentRef.current) === null || _popoverContentRef$cu2 === void 0 ? void 0 : _popoverContentRef$cu2.removeAttribute('data-show');

      // ensure events are removed when not showing popper
      cleanupFunction.current && cleanupFunction.current();
    }
  }, [isPopoverOpen, location]);

  // needed for external override of internal visibility state
  var clickablePopoverState = useMemo(function () {
    if (location === 'none') {
      return false;
    }
    if (isVisible !== undefined) {
      return isVisible;
    }
    return isPopoverOpen;
  }, [isVisible, clickedType, isPopoverOpen]);

  // Cache the current focused dom element across re-renders
  var focusedElRef = useActiveElement(clickablePopoverState);
  var handleClickableOpen = useCallback(function () {
    isVisible === undefined && location !== 'none' && setIsPopoverOpen(true);
    onRequestOpen && onRequestOpen();
    if (type !== 'clickOnly' && location !== 'none') {
      setClickedType('clickOnly');
    }
  }, [isVisible, location, onRequestOpen]);
  var handleClickableClose = function handleClickableClose() {
    onRequestClose && onRequestClose();
    setIsPopoverOpen(false);
    setClickedType(type);
  };
  var finalParentClasses = classnames('C-Popover', _defineProperty(_defineProperty({}, "--type-".concat(type), type), "isDisabled", isVisible === false || location === 'none'), className);
  var finalClasses = classnames('C-Popover__content hc-pa-normal', _defineProperty(_defineProperty(_defineProperty({}, "--location-".concat(location), location), "--size-".concat(size), size), "isVisible", clickablePopoverState), className);
  var hide = function hide() {
    setTimeout(function () {
      setIsPopoverOpen(false);
    }, 200);
  };
  return /*#__PURE__*/React__default.createElement("span", {
    className: "C-Popover__parent"
  }, /*#__PURE__*/React__default.createElement("div", _extends({
    "data-testid": dataTestId ? "".concat(dataTestId, "_parent") : null,
    as: isClickOnly ? 'button' : 'div',
    onClick: clickedType !== 'hoverOnly' && location !== 'none' ? handleClickableOpen : null,
    onMouseEnter: function onMouseEnter() {
      clickedType !== 'clickOnly' && location !== 'none' ? setIsPopoverOpen(true) : null;
    },
    onMouseLeave: function onMouseLeave() {
      clickedType !== 'clickOnly' && location !== 'none' ? hide() : null;
    },
    onFocus: function onFocus() {
      clickedType !== 'clickOnly' && location !== 'none' ? setIsPopoverOpen(true) : null;
    },
    className: finalParentClasses,
    tabIndex: "1",
    ref: popperRef
  }, otherProps), children), /*#__PURE__*/React__default.createElement("div", {
    "data-testid": dataTestId ? dataTestId : null,
    className: finalClasses,
    ref: popoverContentRef,
    onFocus: function onFocus() {
      clickedType !== 'clickOnly' && location !== 'none' ? setIsPopoverOpen(true) : null;
    },
    onMouseEnter: function onMouseEnter() {
      clickedType !== 'clickOnly' && location !== 'none' ? setIsPopoverOpen(true) : null;
    },
    onMouseLeave: function onMouseLeave() {
      clickedType !== 'clickOnly' && location !== 'none' ? setIsPopoverOpen(false) : null;
    },
    onBlur: function onBlur() {
      clickedType === 'clickOnly' && location !== 'none' ? setIsPopoverOpen(false) : null;
    },
    hidden: isClickOnly ? !clickablePopoverState // use "hidden" attr for click-only popovers
    : false
  }, /*#__PURE__*/React__default.createElement(FocusTrap, {
    as: "div",
    active: clickablePopoverState,
    returnFocusTo: focusedElRef,
    onEscape: handleClickableClose,
    "aria-label": otherProps['aria-label'] || 'Popover Content'
  }, content), /*#__PURE__*/React__default.createElement("div", {
    className: "C-Popper__Arrow",
    location: popperLocation,
    "data-testid": dataTestId ? "".concat(dataTestId, "-popperArrow") : null,
    ref: arrowRef
  })), /*#__PURE__*/React__default.createElement(Overlay, {
    onClick: handleClickableClose,
    isVisible: clickedType === 'clickOnly' ? clickablePopoverState : false
  }));
};
Popover.displayName = 'Popover';

export { Popover as default };
